import { ChangeDetectionStrategy, Component, ElementRef, inject, Input, type OnInit, viewChild } from '@angular/core';
import { type Placement } from '@floating-ui/dom';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { type PopoverVariationName, RcpPopoverDirective } from '@reconcept-platform/shared/ui/standalone/popover';
import { delay, take } from 'rxjs';
import { TourService } from '../tour.service';

@Component({
  selector: 'rcp-tour-hotspot',
  templateUrl: './tour-hotspot.component.html',
  styleUrls: ['./tour-hotspot.component.scss'],
  imports: [FontAwesomeModule, RcpPopoverDirective],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '(document:pointerdown)': 'onDocumentPointerDown($event)',
  },
})
export class TourHotspotComponent implements OnInit {
  private tourService = inject(TourService);
  private hostElement = inject(ElementRef).nativeElement as HTMLElement;
  private tooltip = viewChild(RcpPopoverDirective);

  @Input() id: string;
  @Input() dismissable: boolean;
  @Input() position: Placement;
  @Input() variation: PopoverVariationName = 'default';

  ngOnInit() {
    this.showTooltipIfNotDismissed();
  }

  dismiss() {
    if (this.dismissable) {
      this.tourService.storeTourItemDismissed(this.id).subscribe();
    }
  }

  /**
   * A dismissable hotspot is shown unprompted on load, so any click elsewhere on
   * the page should close it. Clicks on the hotspot itself go through `dismiss()`.
   */
  protected onDocumentPointerDown(event: Event) {
    if (!this.dismissable || this.hostElement.contains(event.target as Node)) {
      return;
    }
    // @ts-expect-error TS2532 object possibly undefined
    this.tooltip().hide();
  }

  private showTooltipIfNotDismissed() {
    this.tourService
      .getTourItemWasDismissed(this.id)
      .pipe(
        // small delay to prevent flickering, take 1 to not show on updates
        delay(300),
        take(1),
      )
      .subscribe({
        next: (wasDismissed) => {
          // If the tooltip hasn't been dismissed yet, show it.
          if (!wasDismissed) {
            // @ts-expect-error TS2532 object possibly undefined
            this.tooltip().show();
            if (this.dismissable) {
              this.dismiss();
            }
          }
        },
      });
  }
}
